import { smartLinkPlatformDomains } from 'fym-shared/src/smartlinks/domains';

/**
 * The address shown before an editable slug, split so the host can be picked.
 *
 * The prefix arrives already built by the server (`acme.fym.fm/`), because that
 * is the only place that knows the environment's template. Splitting it here,
 * rather than rebuilding it from the slug and the domain, keeps a local or a
 * custom-domain address intact: neither ends in a platform host, so neither can
 * be split and both stay a plain, unpickable string.
 */
export type PlatformDomainPrefix = {
  /** Everything before the host, e.g. `acme.` — already ends in a dot. */
  head: string;
  /** The host itself, one of `smartLinkPlatformDomains`. */
  domain: string;
  /** Everything after the host, normally the trailing slash. */
  tail: string;
};

export function splitPlatformDomainPrefix(prefix: string): PlatformDomainPrefix | null {
  for (const domain of smartLinkPlatformDomains) {
    const marker = `.${domain}`;
    const index = prefix.indexOf(marker);
    if (index > 0) {
      return {
        head: prefix.slice(0, index + 1),
        domain,
        tail: prefix.slice(index + marker.length),
      };
    }
  }
  return null;
}

/**
 * The same prefix on another platform host.
 *
 * A prefix that carries no platform host — a local one, a custom domain — comes
 * back untouched, because there is nothing there to swap.
 */
export function applyPlatformDomainToPrefix(prefix: string, domain: string): string {
  const parts = splitPlatformDomainPrefix(prefix);
  if (parts === null) {
    return prefix;
  }
  return `${parts.head}${domain}${parts.tail}`;
}
